import React from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FolderTree, Stethoscope, ListChecks, Users, Building2 } from 'lucide-react';
import DiseaseCategoriesTab from '../components/settings/DiseaseCategoriesTab';
import DiseasesTab from '../components/settings/DiseasesTab';
import StatusManagementTab from '../components/settings/StatusManagementTab';
import LeadSourcesTab from '../components/settings/LeadSourcesTab';
import DepartmentsTab from '../components/settings/DepartmentsTab';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageSubtitle = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
`;

const FilterContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-light);
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;
  flex-wrap: wrap;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  ${props => props.$isActive ? `
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  ` : `
    background: white;
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover {
      border-color: var(--primary-color);
      color: var(--primary-color);
    }
  `}
`;

const TabContent = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  box-shadow: var(--shadow-light);
`;

type TabKey = 'disease-categories' | 'diseases' | 'statuses' | 'lead-sources' | 'departments';

const validTabs: TabKey[] = ['disease-categories', 'diseases', 'statuses', 'lead-sources', 'departments'];

const SystemSettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get active tab from URL or default to 'disease-categories'
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = validTabs.includes(tabParam as TabKey)
    ? (tabParam as TabKey)
    : 'disease-categories';

  const handleTabChange = (tabKey: TabKey) => {
    setSearchParams({ tab: tabKey });
  };

  const tabs: { key: TabKey; title: string; icon: typeof FolderTree }[] = [
    { key: 'disease-categories', title: 'קטגוריות מחלות', icon: FolderTree },
    { key: 'diseases', title: 'מחלות', icon: Stethoscope },
    { key: 'statuses', title: 'ניהול סטטוסים', icon: ListChecks },
    { key: 'lead-sources', title: 'סוכנים וארגונים', icon: Users },
    { key: 'departments', title: 'מחלקות', icon: Building2 }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'disease-categories':
        return <DiseaseCategoriesTab />;
      case 'diseases':
        return <DiseasesTab />;
      case 'statuses':
        return <StatusManagementTab />;
      case 'lead-sources':
        return <LeadSourcesTab />;
      case 'departments':
        return <DepartmentsTab />;
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRight>
          <div>
            <PageTitle>הגדרות מערכת</PageTitle>
            <PageSubtitle>הגדרת וניהול כל הרשימות הנפתחות במערכת</PageSubtitle>
          </div>
        </HeaderRight>
      </PageHeader>

      <FilterContainer>
        <TabsContainer>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.key}
                $isActive={activeTab === tab.key}
                onClick={() => handleTabChange(tab.key)}
              >
                <Icon size={16} />
                {tab.title}
              </Tab>
            );
          })}
        </TabsContainer>
      </FilterContainer>

      <TabContent>
        {renderTabContent()}
      </TabContent>
    </PageContainer>
  );
};

export default SystemSettingsPage;
